import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import {
  showSuccessMessage,
  showErrorMessage,
} from "../../shared/Services/toasterService";
import "../../assets/scss/styles.scss";

// Constants
// Matches backend enum: 0 = Invitation, 1 = PasswordReset
const TOKEN_TYPE = { INVITATION: 0, PASSWORD_RESET: 1 };
const LOGO_URL = process.env.REACT_APP_LOGIN_LOGO_URL || "/assets/images/logo/AutoMenuLogo.png";

// Internal state machine for token lifecycle
const STATUS = {
  VALIDATING: "validating",
  VALID: "valid",
  EXPIRED: "expired",
  ALREADY_USED: "already_used",
  DEACTIVATED: "deactivated",
  NOT_FOUND: "not_found",
  NETWORK_ERROR: "network_error",
};

const SILENT_REVALIDATE_MS = 30_000;

// Password strength rules
const RULES = [
  {
    key: "minLength",
    label: "At least 8 characters",
    test: (p) => p.length >= 8,
  },
  {
    key: "uppercase",
    label: "At least one uppercase letter (A-Z)",
    test: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lowercase",
    label: "At least one lowercase letter (a-z)",
    test: (p) => /[a-z]/.test(p),
  },
  {
    key: "number",
    label: "At least one number (0-9)",
    test: (p) => /[0-9]/.test(p),
  },
  {
    key: "special",
    label: "At least one special character (!@#$%^&*...)",
    test: (p) => /[^A-Za-z0-9]/.test(p),
  },
];

// Helpers
/** Format seconds to mm:ss */
const formatTime = (secs) => {
  if (secs < 0) secs = 0;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const formatExpiredAgo = (seconds) => {
  if (!seconds || seconds <= 0) return "recently";
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"} ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  return `${hours} hour${hours === 1 ? "" : "s"} and ${mins} minute${
    mins === 1 ? "" : "s"
  } ago`;
};

// Sub-components
const Logo = () => (
  <div className="auth-logo">
    <img
        className="fusz-logo"
        src={LOGO_URL}
    />
  </div>
);

const CountdownBanner = ({ remainingSeconds }) => {
  const level =
    remainingSeconds <= 60
      ? "danger"
      : remainingSeconds <= 300
        ? "warning"
        : "safe";

  const icon =
    level === "danger"
      ? "fas fa-exclamation-circle"
      : level === "warning"
        ? "fas fa-clock"
        : "fas fa-shield-alt";

  const label =
    level === "danger"
      ? "Urgent – link expires in"
      : level === "warning"
        ? "Warning – link expires in"
        : "Link expires in";

  return (
    <div className={`token-countdown-banner banner-${level}`}>
      <i className={`${icon} countdown-icon`} />
      <span className="countdown-text">{label}</span>
      <span className="countdown-time">{formatTime(remainingSeconds)}</span>
    </div>
  );
};

// Main Component
const PasswordSetupPage = ({ mode = "reset" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") || "";
  const isSetMode = mode === "set";
  const tokenType = isSetMode
    ? TOKEN_TYPE.INVITATION
    : TOKEN_TYPE.PASSWORD_RESET;

  const cfg = {
    set: {
      heading: "Set Your Password",
      subtitle: "Welcome! Create a password to activate your account.",
      submitLabel: "Set Password",
      submittingLabel: "Setting Password...",
      successHeading: "Account Activated!",
      successBody:
        "Your password has been set. You are being sent to the login page…",
      expiredHeading: "Invitation Expired",
      expiredBody:
        "This invitation link has expired. Please contact your administrator to resend the invitation.",
      notFoundHeading: "Invalid Invitation Link",
      notFoundBody:
        "This invitation link is invalid or has already been deleted. Please contact your administrator.",
      usedHeading: "Invitation Already Used",
      usedBody:
        "You have already set your password using this link. Please log in with your credentials.",
      deactivatedHeading: "Account Deactivated",
      deactivatedBody:
        "Your account has been deactivated. Please contact your admin.",
    },
    reset: {
      heading: "Reset Password",
      subtitle: "Enter and confirm your new password below.",
      submitLabel: "Reset Password",
      submittingLabel: "Resetting…",
      successHeading: "Password Reset!",
      successBody:
        "Your password has been reset. You will be redirected to the login page shortly.",
      expiredHeading: "Reset Link Expired",
      expiredBody:
        "This password reset link has expired. Please request a new one.",
      notFoundHeading: "Invalid Reset Link",
      notFoundBody:
        "This password reset link is invalid or has already been used. Please request a new one.",
      usedHeading: "Link Already Used",
      usedBody:
        "This reset link has already been used. Please request a new password reset.",
      deactivatedHeading: "Account Deactivated",
      deactivatedBody:
        "Your account has been deactivated. Please contact your admin.",
    },
  }[mode];

  // Token validation state
  const [tokenStatus, setTokenStatus] = useState(
    token ? STATUS.VALIDATING : STATUS.NOT_FOUND,
  );
  const [tokenData, setTokenData] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [expiredAgoSecs, setExpiredAgoSecs] = useState(0);
  const [networkError, setNetworkError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Refs to avoid stale closures inside intervals
  const statusRef = useRef(tokenStatus);
  const expiryMsRef = useRef(null); // absolute UTC ms of token expiry
  const timeOffsetRef = useRef(0); // ms: Date.now() - serverNow (clock skew)
  const countdownTimerRef = useRef(null);

  // Keep statusRef in sync with state
  useEffect(() => {
    statusRef.current = tokenStatus;
  }, [tokenStatus]);

  // Form state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  // Countdown ticker
  /**
   * Starts (or restarts) the 1-second countdown ticker.
   * Always clears any existing interval first so there is never more than
   * one running at a time (important for silent revalidation calls).
   */
  const startCountdown = useCallback((initialSeconds) => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (initialSeconds <= 0) {
      setRemainingSeconds(0);
      return;
    }

    setRemainingSeconds(initialSeconds);

    countdownTimerRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        let remaining;
        if (expiryMsRef.current) {
          const adjustedNow = Date.now() - timeOffsetRef.current;
          remaining = Math.max(
            0,
            Math.floor((expiryMsRef.current - adjustedNow) / 1000),
          );
        } else {
          remaining = Math.max(0, prev - 1);
        }

        if (remaining <= 0) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          if (statusRef.current === STATUS.VALID) {
            setTokenStatus(STATUS.EXPIRED);
          }
          return 0;
        }

        return remaining;
      });
    }, 1000);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  // Token validation logic
  const processValidationResponse = useCallback(
    (data) => {
      if (data.errorCode === "TOKEN_ALREADY_USED") {
        setTokenStatus(STATUS.ALREADY_USED);
        return;
      }
      if (data.errorCode === "ACCOUNT_DEACTIVATED") {
        setTokenStatus(STATUS.DEACTIVATED);
        return;
      }
      if (data.isExpired || data.errorCode === "TOKEN_EXPIRED") {
        setTokenStatus(STATUS.EXPIRED);
        setExpiredAgoSecs(data.expiredAgoSeconds || 0);
        return;
      }
      if (!data.isValid) {
        setTokenStatus(STATUS.NOT_FOUND);
        return;
      }

      // new Date(null) returns the Unix epoch (0), NOT NaN.
      // Guard against null/undefined/empty before parsing so a missing field
      // doesn't silently produce epoch=0 and make recomputedRemaining=0 → EXPIRED.
      const serverNowMs = data.currentServerTime
        ? new Date(
            data.currentServerTime.endsWith("Z")
              ? data.currentServerTime
              : data.currentServerTime + "Z",
          ).getTime()
        : Date.now();

      const expiryMs = data.expiryDate
        ? new Date(
            data.expiryDate.endsWith("Z")
              ? data.expiryDate
              : data.expiryDate + "Z",
          ).getTime()
        : NaN;

      // Store clock-skew and absolute expiry for drift-free interval ticks
      timeOffsetRef.current = Date.now() - serverNowMs;
      expiryMsRef.current = isNaN(expiryMs) ? null : expiryMs;

      // Use the server's own authoritative remainingSeconds as the seed
      const remaining = data.remainingSeconds ?? 0;

      if (remaining <= 0) {
        setTokenStatus(STATUS.EXPIRED);
        setExpiredAgoSecs(data.expiredAgoSeconds || 0);
        return;
      }

      setTokenData(data);
      setNetworkError(null);
      statusRef.current = STATUS.VALID;
      setTokenStatus(STATUS.VALID);
      startCountdown(remaining);
    },
    [startCountdown],
  );

  /** Full validation — shows loading spinner, used on mount and manual retry */
  const validateToken = useCallback(async () => {
    setTokenStatus(STATUS.VALIDATING);
    setNetworkError(null);
    try {
      const res = await apiManager.post(
        API_ENDPOINTS.EXTERNAL_USER_VALIDATE_TOKEN,
        { token, type: tokenType },
      );
      if (res.status === 200 && res.data) {
        processValidationResponse(res.data);
      } else {
        setTokenStatus(STATUS.NOT_FOUND);
      }
    } catch (err) {
      setTokenStatus(STATUS.NETWORK_ERROR);
      setNetworkError(err || "Network error. Please check your connection.");
    }
  }, [token, tokenType, processValidationResponse]);

  /**
   * Silent revalidation — does NOT reset UI to "validating".
   * Runs every 30 s and just before form submit.
   * On network failure falls back to the client-clock estimate.
   */
  const silentValidate = useCallback(async () => {
    try {
      const res = await apiManager.post(
        API_ENDPOINTS.EXTERNAL_USER_VALIDATE_TOKEN,
        { token, type: tokenType },
      );
      if (res.status === 200 && res.data) {
        processValidationResponse(res.data);
      }
    } catch {
      // Network failure on silent check — recalculate from stored expiry
      if (expiryMsRef.current) {
        const adjustedNow = Date.now() - timeOffsetRef.current;
        const remaining = Math.max(
          0,
          Math.floor((expiryMsRef.current - adjustedNow) / 1000),
        );
        if (remaining === 0 && statusRef.current === STATUS.VALID) {
          setTokenStatus(STATUS.EXPIRED);
        }
      }
    }
  }, [token, tokenType, processValidationResponse]);

  // On mount — initial validation (skip if no token in URL)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) return;
    validateToken();
  }, []); // intentionally run once on mount only

  // Silent auto-revalidate every 30 s while token is VALID
  useEffect(() => {
    if (tokenStatus !== STATUS.VALID) return;
    const id = setInterval(silentValidate, SILENT_REVALIDATE_MS);
    return () => clearInterval(id);
  }, [tokenStatus, silentValidate]);

  // Auto-redirect to login after success
  useEffect(() => {
    if (!isSuccess) return;
    setRedirectCountdown(5);
    const tick = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(tick);
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [isSuccess, navigate]);

  // Form validation
  const ruleResults = useMemo(
    () => RULES.map((rule) => ({ ...rule, passed: rule.test(newPassword) })),
    [newPassword],
  );
  const allRulesPassed = ruleResults.every((r) => r.passed);

  const validateForm = () => {
    let valid = true;
    if (!newPassword) {
      setNewPasswordError("Password is required.");
      valid = false;
    } else if (!allRulesPassed) {
      setNewPasswordError("Password does not meet all requirements.");
      valid = false;
    } else {
      setNewPasswordError("");
    }
    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password.");
      valid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      valid = false;
    } else {
      setConfirmPasswordError("");
    }
    return valid;
  };

  // Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (tokenStatus !== STATUS.VALID) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    // Silent revalidation right before submit (case 8)
    try {
      const res = await apiManager.post(
        API_ENDPOINTS.EXTERNAL_USER_VALIDATE_TOKEN,
        { token, type: tokenType },
      );
      if (
        res.data &&
        (res.data.isExpired || !res.data.isValid || res.data.success === false)
      ) {
        processValidationResponse(res.data);
        showErrorMessage(
          "Your link has expired or is no longer valid. Please request a new one.",
        );
        setIsSubmitting(false);
        return;
      }
    } catch {
      // Network issue on pre-submit check — proceed optimistically
    }

    try {
      const payload = {
        token,
        password: newPassword,
        confirmPassword,
        type: isSetMode ? 0 : 1,
      };

      const res = await apiManager.post(
        API_ENDPOINTS.EXTERNAL_USER_SET_PASSWORD,
        payload,
      );

      if (res.status === 200) {
        if (res.data?.success === false) {
          const errCode = res.data?.errorCode;
          const errMsg = res.data?.message;

          if (errCode === "TOKEN_EXPIRED") {
            setTokenStatus(STATUS.EXPIRED);
          } else if (errCode === "TOKEN_ALREADY_USED") {
            setTokenStatus(STATUS.ALREADY_USED);
          } else {
            showErrorMessage(errMsg || "Operation failed. Please try again.");
          }
          setIsSubmitting(false);
          return;
        }

        setIsSuccess(true);
        showSuccessMessage(
          isSetMode
            ? "Password set successfully!"
            : "Password reset successfully!",
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      const errCode = err?.response?.data?.errorCode;
      const errMsg = err?.response?.data?.message;

      if (status === 400) {
        if (errCode === "TOKEN_EXPIRED") setTokenStatus(STATUS.EXPIRED);
        else if (errCode === "TOKEN_ALREADY_USED")
          setTokenStatus(STATUS.ALREADY_USED);
        else
          showErrorMessage(
            errMsg || "Validation failed. Please check your inputs.",
          );
      } else {
        showErrorMessage(err || "Operation failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render helpers

  const ActionCard = ({ iconClass, iconType, heading, body, children }) => (
    <div className="loginwrapper">
      <div className="auth-card">
        <Logo />
        <div className="token-status-display">
          <span className={`status-icon-wrap ${iconType}`}>
            <i className={iconClass} />
          </span>
          <h2>{heading}</h2>
          <p className="auth-subtitle">{body}</p>
        </div>
        {children}
      </div>
    </div>
  );

  // No token in URL
  if (!token) {
    return (
      <ActionCard
        iconClass="fas fa-unlink"
        iconType="icon-error"
        heading={cfg.notFoundHeading}
        body={cfg.notFoundBody}
      >
        <div className="auth-actions" style={{ marginTop: 20 }}>
          {!isSetMode && (
            <button
              className="btn-primary-action"
              onClick={() => navigate("/forgot-password")}
            >
              Request New Link
            </button>
          )}
          <button
            className={isSetMode ? "btn-primary-action" : "btn-cancel-action"}
            onClick={() => navigate("/")}
          >
            Back to Login
          </button>
        </div>
      </ActionCard>
    );
  }

  // Validating
  if (tokenStatus === STATUS.VALIDATING) {
    return (
      <div className="loginwrapper">
        <div className="auth-card">
          <Logo />
          <div className="auth-validating">
            <div className="auth-spinner" />
            <p>Validating your link…</p>
          </div>
        </div>
      </div>
    );
  }

  // Network error
  if (tokenStatus === STATUS.NETWORK_ERROR) {
    return (
      <div className="loginwrapper">
        <div className="auth-card">
          <Logo />
          <div className="network-error-box">
            <i className="fas fa-wifi error-icon" />
            <p>
              Could not validate your link. Please check your internet
              connection and try again.
              {networkError && (
                <>
                  <br />
                  <small>{networkError}</small>
                </>
              )}
            </p>
            <button
              className="btn-retry"
              onClick={validateToken}
              disabled={isRetrying}
            >
              {isRetrying ? "Retrying…" : "Retry"}
            </button>
          </div>
          <div className="auth-actions" style={{ marginTop: 12 }}>
            <button className="btn-cancel-action" onClick={() => navigate("/")}>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Token expired
  if (tokenStatus === STATUS.EXPIRED) {
    return (
      <ActionCard
        iconClass="fas fa-hourglass-end"
        iconType="icon-expired"
        heading={cfg.expiredHeading}
        body={cfg.expiredBody}
      >
        {expiredAgoSecs > 0 && (
          <span className="expired-ago-label">
            Expired {formatExpiredAgo(expiredAgoSecs)}
          </span>
        )}
        <div className="auth-actions" style={{ marginTop: 20 }}>
          {!isSetMode && (
            <button
              className="btn-primary-action"
              onClick={() => navigate("/forgot-password")}
            >
              Request New Link
            </button>
          )}
          <button
            className={isSetMode ? "btn-primary-action" : "btn-cancel-action"}
            onClick={() => navigate("/")}
          >
            Back to Login
          </button>
        </div>
      </ActionCard>
    );
  }

  // Already used
  if (tokenStatus === STATUS.ALREADY_USED) {
    return (
      <ActionCard
        iconClass="fas fa-check-circle"
        iconType="icon-success"
        heading={cfg.usedHeading}
        body={cfg.usedBody}
      >
        <div className="auth-actions" style={{ marginTop: 20 }}>
          <button className="btn-primary-action" onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </ActionCard>
    );
  }

  // Account deactivated
  if (tokenStatus === STATUS.DEACTIVATED) {
    return (
      <ActionCard
        iconClass="fas fa-user-slash"
        iconType="icon-error"
        heading={cfg.deactivatedHeading}
        body={cfg.deactivatedBody}
      >
        <div className="auth-actions" style={{ marginTop: 20 }}>
          <button className="btn-cancel-action" onClick={() => navigate("/")}>
            Back to Login
          </button>
        </div>
      </ActionCard>
    );
  }

  // Not found
  if (tokenStatus === STATUS.NOT_FOUND) {
    return (
      <ActionCard
        iconClass="fas fa-link"
        iconType="icon-error"
        heading={cfg.notFoundHeading}
        body={cfg.notFoundBody}
      >
        <div className="auth-actions" style={{ marginTop: 20 }}>
          {!isSetMode && (
            <button
              className="btn-primary-action"
              onClick={() => navigate("/forgot-password")}
            >
              Request New Link
            </button>
          )}
          <button
            className={isSetMode ? "btn-primary-action" : "btn-cancel-action"}
            onClick={() => navigate("/")}
          >
            Back to Login
          </button>
        </div>
      </ActionCard>
    );
  }

  // Success
  if (isSuccess) {
    return (
      <ActionCard
        iconClass="fas fa-check-circle"
        iconType="icon-success"
        heading={cfg.successHeading}
        body=""
      >
        <div className="auth-success-message">
          <strong>Success!</strong> {cfg.successBody}
        </div>
        <p className="auth-subtitle" style={{ marginTop: 8 }}>
          Redirecting to login in {redirectCountdown}s…
        </p>
        <div className="auth-actions" style={{ marginTop: 16 }}>
          <button className="btn-primary-action" onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </ActionCard>
    );
  }

  // Main form (STATUS.VALID)
  return (
    <div className="loginwrapper">
      <div className="auth-card">
        <Logo />
        <h2>{cfg.heading}</h2>
        <p className="auth-subtitle">{cfg.subtitle}</p>

        {/* Only show countdown for password reset (not for invitation/set-password flow) */}
        {!isSetMode && remainingSeconds > 0 && (
          <CountdownBanner remainingSeconds={remainingSeconds} />
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {/* Password field */}
          <div className="form-group">
            <label htmlFor="setup-password">
              {isSetMode ? "Password" : "New Password"}
            </label>
            <div className="input-icon-wrapper">
              <i className="fas fa-lock input-icon" />
              <input
                id="setup-password"
                type={showNewPassword ? "text" : "password"}
                placeholder={
                  isSetMode ? "Create a password" : "Enter new password"
                }
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (newPasswordError) setNewPasswordError("");
                }}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="eye-toggle"
                onClick={() => setShowNewPassword((v) => !v)}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                <i
                  className={
                    showNewPassword ? "fas fa-eye-slash" : "fas fa-eye"
                  }
                />
              </button>
            </div>
            {newPasswordError && (
              <p className="field-error">{newPasswordError}</p>
            )}
          </div>

          {/* Live password rules */}
          {newPassword.length > 0 && (
            <div className="password-rules">
              <span>Password must contain:</span>
              <ul>
                {ruleResults.map((rule) => (
                  <li
                    key={rule.key}
                    className={rule.passed ? "valid" : "invalid"}
                  >
                    <i
                      className={
                        rule.passed ? "fas fa-check-circle" : "fas fa-circle"
                      }
                      style={{ marginRight: 6 }}
                    />
                    {rule.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Confirm password field */}
          <div className="form-group">
            <label htmlFor="setup-confirm">Confirm Password</label>
            <div className="input-icon-wrapper">
              <i className="fas fa-lock input-icon" />
              <input
                id="setup-confirm"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (confirmPasswordError) setConfirmPasswordError("");
                }}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="eye-toggle"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                <i
                  className={
                    showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"
                  }
                />
              </button>
            </div>
            {confirmPasswordError && (
              <p className="field-error">{confirmPasswordError}</p>
            )}
          </div>

          <div className="auth-actions">
            <button
              type="submit"
              className="btn-primary-action"
              disabled={isSubmitting}
            >
              {isSubmitting ? cfg.submittingLabel : cfg.submitLabel}
            </button>
            {!isSetMode && (
              <button
                type="button"
                className="btn-cancel-action"
                onClick={() => navigate("/")}
                disabled={isSubmitting}
              >
                Back to Login
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordSetupPage;
